import React, { useEffect, useState } from 'react';
import { getUploadedImagePath, useAppDispatch, useAppState } from '@nutastic/nutastic-react-app';
import TabSlider, { TabSliderContent } from '../../refactor/tabs/TabSlider';
import { useParams } from 'react-router-dom';
import makeFetchCall from '../../utils/makeFetchCall';
import buildTracks from '../../refactor/nu-player/utils/buildTracks.mjs';
import { useNuPlayerContext } from '../../refactor/nu-player/NuPlayerContext';
import { useNuStationData } from '../../hooks/useNuStationData';
import { WebsiteActions } from '../../state/actions.mjs';
import { RelatedContentTab } from '../ContentRelations';
import ContentTrackList from '../ContentTrackList';
import { ContentDescription } from '../ContentDetails';
import ContentNotFoundPage from './ContentNotFoundPage';
import ContentBroadcastTracks from '../ContentBroadcastTracks';
import classnames from 'classnames';

const MUSIC_TYPES = {
    station: "Station",
    playlist: "Playalist",
    pack: "Pack",
    album: "Album",
    mixtape: "Mixtape"
};
const componentClass = 'contentPage';

const ContentMusicPage = () => {
    const appDispatch = useAppDispatch();
    const { isProcessing, authState } = useAppState();
    const [broadcastData, setBroadcastData] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [currentProject, setCurrentProject] = useState(null);
    const { getMusicProjectIdBySlug, getRecentMusicData, musicLibrary } = useNuStationData();
    const { id: contentId } = useParams();
    const { isSubscribed } = authState;
    const {
        insertDiscAndPressPlay,
        onLoadBroadcast,
        playerConfigData = {},
        isPlaying,
        currentTrack
    } = useNuPlayerContext();

    const {
        id: musicProjectId,
        slug: musicProjectSlug,
        image: projectImage,
        title: projectTitle,
        shortDescription,
        description,
        tracks,
        relatedArtists,
        relatedMusic,
        relatedVideos,
        isRadioStation,
        type
    } = currentProject || {};

    const { musicProjectId: currentPlayerMusicId } = playerConfigData;
    const thisMusicIsPlaying = musicProjectId === currentPlayerMusicId && isPlaying;
    const recentPlayData = getRecentMusicData(musicProjectId);
    const isValidRecentData = typeof recentPlayData === 'object' && recentPlayData !== null;
    const recentDataToUse = isValidRecentData ? recentPlayData : {};
    const { track: recentPlayTrack, date: recentPlayDate } = recentDataToUse;
    const requiresSubscription = (musicLibrary || []).find(m => m.id === musicProjectId)?.requiresSubscription || false;

    useEffect(() => {
        setCurrentProject(null);
        const fetchMusic = async () => {
            appDispatch({ type: WebsiteActions.LOADING_PAGE_START });
            try {
                const musicProjectId = getMusicProjectIdBySlug(contentId);
                const musicProject = await makeFetchCall(`/api/load-music-project/${musicProjectId}`);
                setActiveTab(0);
                setCurrentProject(musicProject);
            } catch (e) {
                console.log('Failed to fetch music project', e);
            }
            setTimeout(() => {
                appDispatch({ type: WebsiteActions.LOADING_PAGE_DONE });
            }, 1000);
        }
        // noinspection JSIgnoredPromiseFromCall
        fetchMusic();
    }, [contentId]);

    useEffect(() => {
        const fetchBroadcast = async () => {
            try {
                const broadcastData = await makeFetchCall(`/api/start-broadcast/${musicProjectId}`);
                setBroadcastData(broadcastData);
            } catch (e) {
                console.log('Failed to fetch broadcast information', e);
            }
        }

        if(isRadioStation && !broadcastData) {
            fetchBroadcast();
        } else {
            setBroadcastData(null);
        }
    }, [isRadioStation]);

    const onStartBroadcast = () => {
        onLoadBroadcast({ musicProjectId, musicProjectSlug, projectTitle, isRadioStation });
    };

    const onPressPlay = (trackIndex) => {
        if (!requiresSubscription || isSubscribed) {
            const {tracks, image: mainCover, image_1: thumbnailCover} = currentProject;
            const tracksToLoad = buildTracks(tracks, thumbnailCover || mainCover);
            insertDiscAndPressPlay({
                tracksToLoad,
                trackIndex,
                configData: {
                    musicProjectId,
                    musicProjectSlug,
                    projectTitle,
                    isRadioStation: false
                }
            });
        } else {
            appDispatch({ type: WebsiteActions.ACCOUNT_MODAL_SHOW });
        }
    };

    const buildTabs = () => {
        if (currentProject) {
            const detailsTab = { label: 'Details', key: 0, children: <React.Fragment>
                    <ContentDescription className="mb-5" description={description} />
                    <div className="contentLabel contentLabel--bordered">
                        <span>{isRadioStation ? "Today's Schedule" : `${MUSIC_TYPES[type]} Tracks`}</span>
                    </div>
                    { isRadioStation
                        ? <ContentBroadcastTracks broadcastData={broadcastData} broadcastBlockIndex={ thisMusicIsPlaying ? currentTrack?.blockIndex : null} />
                        : <ContentTrackList tracks={tracks} onClick={(!requiresSubscription || isSubscribed) ? onPressPlay : null} />
                    }
            </React.Fragment>
            };
            const relatedTab = { label: 'Related', key: 1, children: <RelatedContentTab artists={relatedArtists} music={relatedMusic} videos={relatedVideos} /> };
            return [ detailsTab, relatedTab ];
        }
        return null;
    };

    const renderButtons = () => {
        const playButtonFn = isRadioStation ? onStartBroadcast : onPressPlay;
        const playButtonText = isRadioStation ? "Play Station" : `Start ${MUSIC_TYPES[type]}`;
        const playButtonClass = classnames('btn', {
            'btn-white': !thisMusicIsPlaying,
            'btn-outline-white' : thisMusicIsPlaying,
            'bg-red': thisMusicIsPlaying
        });
        const playButton = <button className={playButtonClass} onClick={thisMusicIsPlaying ? null : playButtonFn}>
            { thisMusicIsPlaying ? <span>Now Playing</span> : <span>{playButtonText}</span> }
            { thisMusicIsPlaying ? <i className="fa fa-volume-up" /> : <i className="fa fa-play-circle-o" /> }
        </button>;

        const date = new Date().toLocaleDateString('en-CA');
        const useResumeButton = !thisMusicIsPlaying && !isRadioStation && parseInt(recentPlayTrack || -1) > 0 && (recentPlayDate === date || type !== 'playlist');
        const resumeButton = <button className="btn btn-white" onClick={() => onPressPlay(parseInt(recentPlayTrack))}>
            <span>Resume</span>
            <i className="fa fa-play-circle-o" />
        </button>;
        const startOverButton = <button className="btn bg-red btn-outline-white" onClick={onPressPlay}>
            <i className="fa fa-undo m-0" />
        </button>

        return <div className="buttons">
            { !useResumeButton && playButton }
            { useResumeButton && resumeButton }
            { useResumeButton && startOverButton }
        </div>
    };

    const renderSubscriptionButton = () => {
        return <div className="buttons">
            <button className="btn bg-red btn-outline-white" onClick={() => appDispatch({ type: WebsiteActions.ACCOUNT_MODAL_SHOW })}>
                <span>Members Only</span> <i className="fa fa-lock" />
            </button>
        </div>
    };

    return (!!currentProject && !isProcessing) ? <article className={componentClass}>
        <div className={`${componentClass}__header`}>
            <img src={getUploadedImagePath(projectImage)} alt={currentProject?.title} className={`${componentClass}__musicCover`} />
            <div className={`${componentClass}__headerInfo musicLayout`}>
                <h1>{ projectTitle }</h1>
                { shortDescription && <p>{ shortDescription }</p> }
                { (!requiresSubscription || isSubscribed) ? renderButtons() : renderSubscriptionButton() }
            </div>
        </div>
        <div className={`${componentClass}__tabs`}>
            <TabSlider tabs={buildTabs()} onChange={setActiveTab} activeKey={activeTab} />
            <div className="container">
                <TabSliderContent tabs={buildTabs()} activeKey={activeTab} />
            </div>
        </div>
    </article> : <ContentNotFoundPage isProcessing={isProcessing} /> ;
};

export default ContentMusicPage;
